/**
 * Hybrid BASE reconstruction for ghost commits.
 *
 * When a patch's base generation tree is unreachable (GC'd after squash merge,
 * shallow clone), we reconstruct BASE and THEIRS from the unified diff and
 * the current OURS content on disk.
 *
 * The unified diff encodes:
 *   - BASE = context lines + removed lines
 *   - THEIRS = context lines + added lines
 *
 * By matching context lines against OURS, we anchor hunks to positions in the
 * current file and fill gaps with OURS content, producing hybrid BASE and
 * THEIRS suitable for threeWayMerge().
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DiffHunk {
    /** 1-based line number in the old (BASE) file where this hunk starts */
    oldStart: number;
    /** Number of lines in the old (BASE) file this hunk spans */
    oldCount: number;
    /** 1-based line number in the new (THEIRS) file where this hunk starts */
    newStart: number;
    /** Number of lines in the new (THEIRS) file this hunk spans */
    newCount: number;
    /** Ordered entries: context lines, removed lines, added lines */
    lines: HunkLine[];
}

export type HunkLine =
    | { type: "context"; content: string }
    | { type: "remove"; content: string }
    | { type: "add"; content: string };

export interface LocatedHunk {
    hunk: DiffHunk;
    /** 0-based index in OURS lines where this hunk's context starts */
    oursOffset: number;
    /** Number of OURS lines this hunk covers */
    oursSpan: number;
}

export interface ReconstructionResult {
    base: string;
    theirs: string;
}

// ---------------------------------------------------------------------------
// 1. Hunk Parser
// ---------------------------------------------------------------------------

/**
 * Parse a unified diff (for a single file) into structured hunks.
 * Input should start with `diff --git` and contain `@@` hunk headers.
 */
export function parseHunks(fileDiff: string): DiffHunk[] {
    const lines = fileDiff.split("\n");
    const hunks: DiffHunk[] = [];
    let currentHunk: DiffHunk | null = null;

    for (const line of lines) {
        const headerMatch = line.match(
            /^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@/
        );
        if (headerMatch) {
            if (currentHunk) {
                hunks.push(currentHunk);
            }
            currentHunk = {
                oldStart: parseInt(headerMatch[1]!, 10),
                oldCount: headerMatch[2] != null ? parseInt(headerMatch[2], 10) : 1,
                newStart: parseInt(headerMatch[3]!, 10),
                newCount: headerMatch[4] != null ? parseInt(headerMatch[4], 10) : 1,
                lines: []
            };
            continue;
        }

        if (!currentHunk) continue;

        // Skip metadata lines
        if (
            line.startsWith("diff --git") ||
            line.startsWith("index ") ||
            line.startsWith("---") ||
            line.startsWith("+++") ||
            line.startsWith("old mode") ||
            line.startsWith("new mode") ||
            line.startsWith("similarity index") ||
            line.startsWith("rename from") ||
            line.startsWith("rename to") ||
            line.startsWith("new file mode") ||
            line.startsWith("deleted file mode")
        ) {
            continue;
        }

        if (line === "\\ No newline at end of file") {
            continue;
        }

        if (line.startsWith("-")) {
            currentHunk.lines.push({ type: "remove", content: line.slice(1) });
        } else if (line.startsWith("+")) {
            currentHunk.lines.push({ type: "add", content: line.slice(1) });
        } else if (line.startsWith(" ") || line === "") {
            // Context line. A bare empty string is a context line for an empty line
            // (unified diff uses " " prefix but trailing whitespace stripping may remove it).
            currentHunk.lines.push({
                type: "context",
                content: line.startsWith(" ") ? line.slice(1) : line
            });
        }
    }

    if (currentHunk) {
        hunks.push(currentHunk);
    }

    return hunks;
}

// ---------------------------------------------------------------------------
// 2. Context Matcher
// ---------------------------------------------------------------------------

function extractLeadingContext(hunk: DiffHunk): string[] {
    const result: string[] = [];
    for (const line of hunk.lines) {
        if (line.type !== "context") break;
        result.push(line.content);
    }
    return result;
}

function extractTrailingContext(hunk: DiffHunk): string[] {
    const result: string[] = [];
    for (let i = hunk.lines.length - 1; i >= 0; i--) {
        if (hunk.lines[i]!.type !== "context") break;
        result.unshift(hunk.lines[i]!.content);
    }
    return result;
}

function countOursLinesBeforeTrailing(hunk: DiffHunk): number {
    let count = 0;
    const trailingStart = findTrailingContextStart(hunk);
    for (let i = 0; i < trailingStart; i++) {
        if (hunk.lines[i]!.type === "context") count++;
    }
    return count;
}

function findTrailingContextStart(hunk: DiffHunk): number {
    let i = hunk.lines.length - 1;
    while (i >= 0 && hunk.lines[i]!.type === "context") {
        i--;
    }
    return i + 1;
}

function matchesAt(needle: string[], haystack: string[], offset: number): boolean {
    for (let i = 0; i < needle.length; i++) {
        if (haystack[offset + i] !== needle[i]) return false;
    }
    return true;
}

/**
 * Find a sequence of context lines in OURS, starting search near `hint`.
 * Returns 0-based index of the first matching line, or -1 if not found.
 */
function findContextInOurs(
    contextLines: string[],
    oursLines: string[],
    minIndex: number,
    hint: number
): number {
    const SEARCH_WINDOW = 200;
    const maxStart = oursLines.length - contextLines.length;

    const clampedHint = Math.max(minIndex, Math.min(hint, maxStart));

    // Check hint position first, then expand outward symmetrically
    if (clampedHint >= minIndex && clampedHint <= maxStart) {
        if (matchesAt(contextLines, oursLines, clampedHint)) {
            return clampedHint;
        }
    }
    for (let delta = 1; delta <= SEARCH_WINDOW; delta++) {
        for (const sign of [1, -1] as const) {
            const idx = clampedHint + delta * sign;
            if (idx < minIndex || idx > maxStart) continue;
            if (matchesAt(contextLines, oursLines, idx)) {
                return idx;
            }
        }
    }

    return -1;
}

/**
 * Compute how many lines this hunk spans in OURS.
 */
function computeOursSpan(
    hunk: DiffHunk,
    oursLines: string[],
    oursOffset: number
): number {
    const leading = extractLeadingContext(hunk);
    const trailing = extractTrailingContext(hunk);

    if (trailing.length === 0) {
        const contextCount = hunk.lines.filter(
            (l) => l.type === "context"
        ).length;
        return Math.min(contextCount, oursLines.length - oursOffset);
    }

    // Find trailing context starting after leading context
    const searchStart = oursOffset + leading.length;
    for (let i = searchStart; i <= oursLines.length - trailing.length; i++) {
        if (matchesAt(trailing, oursLines, i)) {
            return i + trailing.length - oursOffset;
        }
    }

    // Trailing context not found — fall back to context-only estimate
    const contextCount = hunk.lines.filter(
        (l) => l.type === "context"
    ).length;
    return Math.min(contextCount, oursLines.length - oursOffset);
}

/**
 * Locate each hunk's position in OURS by matching context lines.
 * Returns null if any hunk cannot be located.
 */
export function locateHunksInOurs(
    hunks: DiffHunk[],
    oursLines: string[]
): LocatedHunk[] | null {
    const located: LocatedHunk[] = [];
    let minOursIndex = 0;

    for (const hunk of hunks) {
        const contextLines = extractLeadingContext(hunk);
        let oursOffset: number;

        if (contextLines.length > 0) {
            const found = findContextInOurs(
                contextLines,
                oursLines,
                minOursIndex,
                hunk.newStart - 1
            );
            if (found === -1) {
                // Try trailing context as fallback
                const trailingContext = extractTrailingContext(hunk);
                if (trailingContext.length > 0) {
                    const trailingFound = findContextInOurs(
                        trailingContext,
                        oursLines,
                        minOursIndex,
                        hunk.newStart - 1
                    );
                    if (trailingFound === -1) return null;
                    const nonTrailingCount = countOursLinesBeforeTrailing(hunk);
                    oursOffset = trailingFound - nonTrailingCount;
                    if (oursOffset < minOursIndex) return null;
                } else {
                    return null;
                }
            } else {
                oursOffset = found;
            }
        } else if (hunk.oldStart === 1 && hunk.oldCount === 0) {
            // Pure addition at start of file (or to empty file)
            oursOffset = 0;
        } else {
            // No context lines — use @@ header as best guess
            oursOffset = Math.max(hunk.newStart - 1, minOursIndex);
        }

        const oursSpan = computeOursSpan(hunk, oursLines, oursOffset);

        located.push({ hunk, oursOffset, oursSpan });
        minOursIndex = oursOffset + oursSpan;
    }

    return located;
}

// ---------------------------------------------------------------------------
// 3. Assembler
// ---------------------------------------------------------------------------

/**
 * Assemble hybridBASE and hybridTHEIRS from located hunks and OURS.
 *
 * In gaps between hunks, BASE and THEIRS both get OURS content (3-way merge
 * treats gaps as "no change"). In hunk regions, BASE = context + removed,
 * THEIRS = context + added.
 */
export function assembleHybrid(
    locatedHunks: LocatedHunk[],
    oursLines: string[]
): ReconstructionResult {
    const baseLines: string[] = [];
    const theirsLines: string[] = [];
    let oursCursor = 0;

    for (const { hunk, oursOffset, oursSpan } of locatedHunks) {
        // Gap before this hunk: fill with OURS content
        if (oursOffset > oursCursor) {
            const gapLines = oursLines.slice(oursCursor, oursOffset);
            baseLines.push(...gapLines);
            theirsLines.push(...gapLines);
        }

        // Hunk region: extract BASE lines and THEIRS lines from the diff
        for (const line of hunk.lines) {
            switch (line.type) {
                case "context":
                    baseLines.push(line.content);
                    theirsLines.push(line.content);
                    break;
                case "remove":
                    baseLines.push(line.content);
                    break;
                case "add":
                    theirsLines.push(line.content);
                    break;
            }
        }

        oursCursor = oursOffset + oursSpan;
    }

    // Trailing gap after last hunk
    if (oursCursor < oursLines.length) {
        const gapLines = oursLines.slice(oursCursor);
        baseLines.push(...gapLines);
        theirsLines.push(...gapLines);
    }

    return {
        base: baseLines.join("\n"),
        theirs: theirsLines.join("\n")
    };
}

// ---------------------------------------------------------------------------
// 4. Top-Level Entry Point
// ---------------------------------------------------------------------------

/**
 * Attempt to reconstruct BASE and THEIRS from a unified diff and OURS content.
 * Used when the patch's base generation tree is unreachable (ghost commit).
 *
 * Returns null if reconstruction fails (context mismatch, new/deleted file).
 */
export function reconstructFromGhostPatch(
    fileDiff: string,
    ours: string
): ReconstructionResult | null {
    const hunks = parseHunks(fileDiff);

    if (hunks.length === 0) {
        return null;
    }

    // Pure new-file diff — use extractNewFileFromPatch instead
    const isPureAddition = hunks.every(
        (h) => h.oldCount === 0 && h.lines.every((l) => l.type !== "remove")
    );
    if (isPureAddition) {
        return null;
    }

    // Pure deletion
    const isPureDeletion = hunks.every(
        (h) => h.newCount === 0 && h.lines.every((l) => l.type !== "add")
    );
    if (isPureDeletion) {
        const baseLines: string[] = [];
        for (const hunk of hunks) {
            for (const line of hunk.lines) {
                if (line.type === "context" || line.type === "remove") {
                    baseLines.push(line.content);
                }
            }
        }
        return {
            base: baseLines.join("\n"),
            theirs: ""
        };
    }

    const oursLines = ours.split("\n");
    const located = locateHunksInOurs(hunks, oursLines);

    if (!located) {
        return null;
    }

    return assembleHybrid(located, oursLines);
}
